import { Alert, Box, Button, Text, Tooltip } from '@factorialco/deprecated-design-system'
import { TimeoffCurrentEmployeeDataFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import ExpiredTrialChecker from 'modules/core/components/ExpiredTrialChecker'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { DelegateApproverSelector } from 'modules/approvals/components/DelegateApproverSelector'
import { useAccessApprovalFlows } from 'modules/approvals/hooks/useAccessApprovalFlows'
import BulkLeavesContent from 'modules/timeoff/components/BulkLeavesContent'
import useButtonCopy from 'modules/timeoff/hooks/useButtonCopy'
import useCurrentEmployeeRole from 'modules/timeoff/hooks/useCurrentEmployeeRole'
import useFetchLeaveTypes from 'modules/timeoff/hooks/useFetchLeaveTypes'
import useForms from 'modules/timeoff/hooks/useForms'
import useLabourUnits from 'modules/timeoff/hooks/useLabourUnits'
import useModalProps from 'modules/timeoff/hooks/useModalProps'
import type LeaveDocumentForm from 'modules/timeoff/lib/DocumentForm'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { LeaveTypes } from 'modules/timeoff/store/collections/leaveTypes'
import leaves from 'modules/timeoff/store/collections/leaves'

interface Props {
  onClose: (year: number) => void
  currentEmployee: TimeoffCurrentEmployeeDataFragment
}

const useCreateBulkLeaves = (form: LeaveForm, onCloseYear: () => void) => {
  const { running: isProcessing, performAction: onSubmit } = useAsyncAction(
    async () => {
      const payload = {
        ...form.data(),
        hours_amount_in_cents: form.hoursAmount(),
      }

      const { success } = await leaves.rpcCreateMultipleLeaves(
        payload,
        form.get('employees_for_bulk_creation').value.map((employee: { id: number }) => employee.id)
      )

      if (success) onCloseYear()
    },
    {
      successFlash: false,
    }
  )

  return {
    onSubmit,
    isProcessing,
  }
}

const isSubmitEnabled = (
  form: LeaveForm,
  documentForm: LeaveDocumentForm,
  leaveTypes: LeaveTypes
) => {
  const formLeaveTypeId = form.get('leave_type_id').value
  const halfDay = form.get('half_day').value
  const hourAmount = form.get('hours_amount_in_cents').value

  const selectedLeaveType = leaveTypes.get(Number(formLeaveTypeId))

  if (!selectedLeaveType) return true

  const attachmentOptional = selectedLeaveType.isAttachmentOptional
  const medicalLeaveDocument = form.get('medical_leave_document').value
  const medicalLeaveDocumentFile = documentForm.get('file').value
  const isDetailsRequired = selectedLeaveType?.attributes.details_required
  const hasLeaveDescription = form.get('description').value
  const absurdStartTime = !form.startTimeHasValue && !halfDay && !!hourAmount
  const hasStartOnDate = form.get('start_on').value

  if (isDetailsRequired) {
    return selectedLeaveType?.isMedicalLeave
      ? hasLeaveDescription && (attachmentOptional || medicalLeaveDocument)
      : hasLeaveDescription && (attachmentOptional || medicalLeaveDocumentFile)
  }

  if (absurdStartTime) return false

  if (selectedLeaveType?.isMedicalLeave) {
    return attachmentOptional || medicalLeaveDocument
  }

  return hasStartOnDate && (attachmentOptional || medicalLeaveDocumentFile)
}

const BulkAddLeavesModal = ({ onClose, currentEmployee }: Props) => {
  const {
    company: { subscription },
  } = useCurrentSession()

  const {
    leaveTypes,
    isLoading: isLeaveTypesLoading,
    selectedLeaveType,
  } = useFetchLeaveTypes({
    loadingLeave: false,
    active: true,
  })

  const { form, documentForm, year } = useForms({
    selectedLeaveType,
  })

  const employeeRole = useCurrentEmployeeRole({
    form,
    canApproveLeaves: currentEmployee._policies?.approveLeaves.can ?? false,
    hasTimeoffManager: false,
  })

  const modalProps = useModalProps({
    form,
    canApproveLeaves: currentEmployee._policies?.approveLeaves.can ?? false,
    timeoffManagerFullName: null,
    isMedical: false,
    hasManualApprovalGroup: false,
  })

  const { approvalFlows } = useAccessApprovalFlows('timeoff', currentEmployee.access.id)

  const labourUnits = useLabourUnits({
    form,
    employeeTimeoffPolicyId: form.get('policy_id').value,
  })

  const onCloseYear = () => {
    onClose(year)
  }

  const setSkipEauCheckValue = () => {
    form.get('skip_eau_check').set(null)
  }

  const { onSubmit, isProcessing } = useCreateBulkLeaves(form, onCloseYear)

  const isLoading = isProcessing || isLeaveTypesLoading
  const submitEnabled = isSubmitEnabled(form, documentForm, leaveTypes)

  const onSubmitTracked = () => {
    // The user will only get this error is he's been playing with the HTML
    // inspector and removed the disabled attribute from the submit button
    if (!submitEnabled) {
      return
    }
    track(trackingEvents.timeoff.addTimeOffSubmit, {
      from_shifts: false,
    })

    setSkipEauCheckValue()
    onSubmit()
  }

  const hasSelectedEmployees = form.get('employees_for_bulk_creation').value.length > 0

  const regularButton = useButtonCopy({
    form,
    isCurrentEmployee: false,
    labourUnits,
    currentEmployeeRole: employeeRole,
    showSelectEmployee: !hasSelectedEmployees,
  })

  const isDisabled = isLoading || !submitEnabled || !hasSelectedEmployees

  const willAutoApprove = currentEmployee._policies?.approveLeaves.can ?? false
  const canDelegateApprover =
    !hasFeature('dev_disable_delegate_approval') &&
    (currentEmployee._policies?.approveLeaves.can || approvalFlows.length > 0)

  return (
    <ExpiredTrialChecker subscription={subscription} onClose={() => onCloseYear()}>
      <Modal
        illustration={modalProps.illustration}
        title={modalProps.title}
        description={modalProps.description}
        disableFocusTrap
        onClose={() => onCloseYear()}
        onSubmit={onSubmitTracked}
        footer={
          <>
            {!regularButton.tooltip && (
              <Button submit disabled={isDisabled}>
                {regularButton.button}
              </Button>
            )}

            {!!regularButton.tooltip && (
              <Tooltip
                attachment="bottom center"
                targetAttachment="top center"
                type="brand"
                size="normal"
                open={regularButton.tooltipOpen}
                toggle={regularButton.toggleTooltip}
              >
                <Button disabled={isLoading || isDisabled} submit>
                  {regularButton.button}
                </Button>
                <Tooltip.Pad>{regularButton.tooltip}</Tooltip.Pad>
              </Tooltip>
            )}
          </>
        }
      >
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <BulkLeavesContent
              form={form}
              labourUnits={labourUnits}
              leaveTypes={leaveTypes}
              year={year}
            />
            <Box gap="s8">
              {canDelegateApprover && (
                <DelegateApproverSelector
                  approver={null}
                  category="timeoff"
                  employeeAccessId={currentEmployee.access.id}
                  field={form.get('delegated_approver_id')}
                  startsOn={form.get('start_on').value}
                  finishOn={form.get('finish_on').value}
                />
              )}
              {willAutoApprove && (
                <Alert color="warning">
                  <Box flexDirection="row" justifyContent="center">
                    <Text.Normal>
                      {i18n.t('time_off.employee.add_new.auto_approval_flow')}
                    </Text.Normal>
                  </Box>
                </Alert>
              )}
            </Box>
          </>
        )}
      </Modal>
    </ExpiredTrialChecker>
  )
}

export default observer(BulkAddLeavesModal)
